import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCoachView } from '../../contexts/CoachViewContext';
import messageService from '../../services/endpoints/messageService';
import { MessageContextType, Message, UnreadMessageSummary } from '../../types';

interface CoachCommentThreadProps {
  contextType: MessageContextType;
  contextId: string;
  label?: string;
  /** If true the thread starts expanded (e.g. when opened via a coach-note pill click). */
  defaultExpanded?: boolean;
  /** Override the URL-derived coach context. Use when rendering outside a viewAs URL (e.g., dashboard drawers). */
  forceRelationshipId?: string;
  forceViewAsName?: string;
}

/**
 * Inline coach↔athlete comment thread anchored to a specific record.
 *
 * Coach mode  : visible when isCoachView=true & relationshipId is in the URL.
 * Athlete mode: visible for all athletes — uses GET /messages/by-context.
 *
 * The collapsed pill shows an amber unread-count badge when the shared
 * ['unread-messages'] React Query cache reports pending notes for this item.
 */
const CoachCommentThread: React.FC<CoachCommentThreadProps> = ({
  contextType,
  contextId,
  label = 'entry',
  defaultExpanded = false,
  forceRelationshipId,
  forceViewAsName,
}) => {
  const { isCoachView: ctxIsCoachView, relationshipId: ctxRelId, viewAsName: ctxViewAsName } = useCoachView();

  // forceRelationshipId lets callers outside the viewAs URL flow (e.g. dashboard drawers) use coach mode
  const isCoachView    = forceRelationshipId ? true : ctxIsCoachView;
  const relationshipId = forceRelationshipId ?? ctxRelId;
  const viewAsName     = forceViewAsName ?? ctxViewAsName;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [draft, setDraft] = useState('');
  const queryClient = useQueryClient();

  const coachMode = isCoachView && !!relationshipId;
  const athleteMode = !isCoachView;

  // ── Unread badge — reads from the already-cached unread-summary (zero cost) ──
  const { data: unreadSummaries = [] } = useQuery<UnreadMessageSummary[]>({
    queryKey: ['unread-messages'],
    queryFn: messageService.getUnreadSummary,
    staleTime: 30_000,
  });

  const unreadCount = unreadSummaries
    .filter(
      (s) =>
        s.contextId === contextId &&
        (s.contextType as string) === (contextType as string)
    )
    .reduce((sum, s) => sum + s.unreadCount, 0);

  // ── Full thread — only fetched when the panel is expanded ──────────────────
  const queryKey = coachMode
    ? ['coach-comments', relationshipId, contextType, contextId]
    : ['athlete-comments', contextType, contextId];

  const { data: messages = [], isLoading } = useQuery<Message[]>({
    queryKey,
    queryFn: coachMode
      ? () => messageService.getMessages(relationshipId!, contextType, contextId)
      : () => messageService.getMessagesByContext(contextType, contextId),
    enabled: expanded,
    staleTime: 30_000,
  });

  // For athlete replies: extract the relationshipId from the thread
  const athleteRelId =
    !coachMode && messages.length > 0 ? messages[0].relationshipId : null;

  const effectiveRelId = coachMode ? relationshipId! : (athleteRelId ?? '');

  const sendMutation = useMutation({
    mutationFn: () =>
      messageService.sendMessage({
        relationshipId: effectiveRelId,
        body: draft.trim(),
        contextType,
        contextId,
      }),
    onSuccess: () => {
      setDraft('');
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['unread-messages'] });
      queryClient.invalidateQueries({ queryKey: ['unread-summary'] });
    },
  });

  if (!coachMode && !athleteMode) return null;

  const canSend = coachMode || !!athleteRelId;
  const headerLabel = coachMode
    ? `Coach Notes — ${viewAsName ?? 'Athlete'}`
    : 'Coach Notes';

  const handleSend = () => {
    if (!draft.trim() || sendMutation.isPending || !canSend) return;
    sendMutation.mutate();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Collapsed pill ─────────────────────────────────────────────────────────
  if (!expanded) {
    return (
      <div className="mt-3 border-t border-dark-700 pt-3">
        <button
          onClick={() => setExpanded(true)}
          className={`flex items-center gap-2 text-xs font-medium transition-colors rounded-full px-3 py-1 ${
            unreadCount > 0
              ? 'bg-amber-500/20 border border-amber-500/50 text-amber-400 hover:bg-amber-500/30'
              : 'bg-dark-700 border border-dark-600 text-blue-400 hover:text-blue-300 hover:bg-dark-600'
          }`}
        >
          <span>💬</span>
          <span>Coach notes</span>
          {unreadCount > 0 && (
            <span className="bg-amber-500 text-black text-xs font-bold rounded-full px-1.5 py-0.5 leading-none">
              {unreadCount} new
            </span>
          )}
          {unreadCount === 0 && (
            <span className="text-gray-500">— click to view / add</span>
          )}
        </button>
      </div>
    );
  }

  // ── Expanded thread ────────────────────────────────────────────────────────
  return (
    <div className="mt-3 border-t border-dark-700 pt-3 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-blue-400 uppercase tracking-wide">
          {headerLabel}
        </span>
        <button
          onClick={() => setExpanded(false)}
          className="text-gray-500 hover:text-gray-300 text-xs"
        >
          ✕ collapse
        </button>
      </div>

      {/* Message thread */}
      {isLoading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : messages.length === 0 ? (
        <p className="text-xs text-gray-500 italic">
          {coachMode
            ? 'No notes yet. Add the first comment below.'
            : 'No coach notes yet for this entry.'}
        </p>
      ) : (
        <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
          {messages.map((m) => (
            <div
              key={m.messageId}
              className={`rounded-lg px-3 py-2 text-sm ${
                m.senderIsCoach
                  ? 'bg-gold-900/20 border border-gold-600/20 text-gold-200 ml-0 mr-6'
                  : 'bg-dark-700 border border-dark-600 text-gray-200 ml-6 mr-0'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`text-xs font-semibold ${
                    m.senderIsCoach ? 'text-gold-400' : 'text-blue-400'
                  }`}
                >
                  {m.senderIsCoach ? '🧑‍💼 Coach' : `🏋️ ${m.senderName}`}
                </span>
                <span className="text-xs text-gray-500">
                  {new Date(m.sentAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
              <p className="whitespace-pre-wrap break-words">{m.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* Compose — coaches always, athletes only when a thread exists */}
      {canSend && (
        <div className="flex gap-2">
          <textarea
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Add a note about this ${label}… (Ctrl+Enter to send)`}
            className="flex-1 bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-sm text-white resize-none focus:outline-none focus:border-gold-500"
          />
          <button
            onClick={handleSend}
            disabled={!draft.trim() || sendMutation.isPending}
            className="px-3 py-2 bg-gold-500 hover:bg-gold-400 text-black text-xs font-bold rounded-lg disabled:opacity-40 transition-colors self-end"
          >
            {sendMutation.isPending ? '…' : 'Send'}
          </button>
        </div>
      )}
    </div>
  );
};

export default CoachCommentThread;
